import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Box, Typography, Divider, Autocomplete,
  FormControl, InputLabel, Select, MenuItem, Tooltip, Alert,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { useDescansoMedicoStore } from '../store/descansoMedicoStore';
import { TrabajadorService } from '../../trabajadores/services/trabajadorService';
import type { Trabajador } from '../../trabajadores/types/trabajador';
import type { DescansoMedico } from '../types/descansoMedico';
import { TIPOS_DESCANSO, ESTADOS_DESCANSO, initialDescansoMedico } from '../types/descansoMedico';

interface Props {
  open: boolean;
  onClose: (saved?: boolean) => void;
  descanso: DescansoMedico | null;
  idPeriodo: number;
}

export default function EditDescansoMedico({ open, onClose, descanso, idPeriodo }: Props) {
  const { createDescanso, updateDescanso } = useDescansoMedicoStore();
  const [form, setForm] = useState<Partial<DescansoMedico>>(initialDescansoMedico);
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [selectedTrabajador, setSelectedTrabajador] = useState<Trabajador | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadTrabajadores();
      if (descanso) {
        setForm({ ...descanso });
      } else {
        setForm({ ...initialDescansoMedico, idPeriodo });
      }
    }
  }, [open, descanso, idPeriodo]);

  const loadTrabajadores = async () => {
    try {
      const data = await TrabajadorService.getAll();
      setTrabajadores(data.filter(t => t.activo));
      if (descanso) {
        const trab = data.find(t => t.idTrabajador === descanso.idTrabajador);
        setSelectedTrabajador(trab ?? null);
      } else {
        setSelectedTrabajador(null);
      }
    } catch { /* handled */ }
  };

  const handleChange = (field: string, value: string | number | null) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Calcular días automáticamente cuando cambian fechas
  const diasCalculados = (() => {
    if (!form.fechaInicio || !form.fechaFin) return 0;
    const inicio = new Date(form.fechaInicio);
    const fin = new Date(form.fechaFin);
    const diff = Math.floor((fin.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  })();

  const handleSave = async () => {
    if (!selectedTrabajador) {
      setError('Seleccione un trabajador');
      return;
    }
    if (!form.fechaInicio || !form.fechaFin) {
      setError('Las fechas son obligatorias');
      return;
    }
    if (diasCalculados < 1) {
      setError('La fecha fin debe ser igual o posterior a la fecha inicio');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const data: Partial<DescansoMedico> = {
        ...form,
        idTrabajador: selectedTrabajador.idTrabajador,
        idPeriodo,
        diasDescanso: diasCalculados,
      };

      if (descanso) {
        await updateDescanso(descanso.idDescansoMedico, data);
      } else {
        await createDescanso(data);
      }
      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!descanso;

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? 'Editar' : 'Nuevo'} Descanso Médico</DialogTitle>
      <DialogContent>
        {/* Trabajador */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', mt: 1 }}>
          Trabajador
        </Typography>
        <Autocomplete
          size="small"
          options={trabajadores}
          getOptionLabel={(t) => `${t.personal?.personaContacto ?? ''} (${t.idTrabajador})`}
          value={selectedTrabajador}
          onChange={(_, val) => setSelectedTrabajador(val)}
          renderInput={(params) => <TextField {...params} label="Buscar trabajador" />}
          sx={{ mb: 2 }}
          disabled={isEditing}
        />

        <Divider sx={{ my: 1 }} />

        {/* Tipo y Estado */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Tipo y Estado
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Tipo Descanso</InputLabel>
            <Select value={form.tipoDescanso ?? 'ENFERMEDAD_COMUN'} label="Tipo Descanso"
              onChange={(e) => handleChange('tipoDescanso', e.target.value)}>
              {TIPOS_DESCANSO.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select value={form.estado ?? 'REGISTRADO'} label="Estado"
              onChange={(e) => handleChange('estado', e.target.value)}>
              {ESTADOS_DESCANSO.map(e => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Fechas y Días */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Fechas y Días
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
          <TextField label="Fecha Inicio" type="date" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.fechaInicio?.substring(0, 10) ?? ''}
            onChange={(e) => handleChange('fechaInicio', e.target.value)} />
          <TextField label="Fecha Fin" type="date" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.fechaFin?.substring(0, 10) ?? ''}
            onChange={(e) => handleChange('fechaFin', e.target.value)} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField label="Total Días" size="small" value={diasCalculados} InputProps={{ readOnly: true }}
            sx={{ '& input': { fontWeight: 700 } }} />
          <Tooltip title="Primeros 20 días acumulados en el año: empleador paga remuneración" arrow>
            <TextField label="Días Empleador" size="small"
              value={form.diasEmpleador ?? '-'}
              InputProps={{ readOnly: true, endAdornment: <InfoIcon fontSize="small" color="action" /> }} />
          </Tooltip>
          <Tooltip title="A partir del día 21 acumulado: EsSalud subsidia (incapacidad temporal)" arrow>
            <TextField label="Días Subsidiados" size="small"
              value={form.diasSubsidiados ?? '-'}
              InputProps={{ readOnly: true, endAdornment: <InfoIcon fontSize="small" color="info" /> }} />
          </Tooltip>
        </Box>
        {isEditing && (form.diasSubsidiados ?? 0) > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Este trabajador tiene {form.diasSubsidiados} día(s) subsidiados por EsSalud en este descanso.
          </Alert>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Certificado CITT */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Certificado de Incapacidad (CITT)
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField label="N° CITT" size="small" fullWidth
            value={form.numeroCITT ?? ''}
            onChange={(e) => handleChange('numeroCITT', e.target.value)} />
          <TextField label="Fecha Emisión CITT" type="date" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.fechaEmisionCITT?.substring(0, 10) ?? ''}
            onChange={(e) => handleChange('fechaEmisionCITT', e.target.value)} />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Datos Médicos */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Datos Médicos
        </Typography>
        <TextField label="Diagnóstico" size="small" fullWidth multiline rows={2} sx={{ mb: 1.5 }}
          value={form.diagnostico ?? ''}
          onChange={(e) => handleChange('diagnostico', e.target.value)} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField label="Médico / Institución" size="small" fullWidth
            value={form.medicoInstitucion ?? ''}
            onChange={(e) => handleChange('medicoInstitucion', e.target.value)} />
          <TextField label="Monto Subsidio (S/)" type="number" size="small" fullWidth
            value={form.montoSubsidio ?? ''}
            onChange={(e) => handleChange('montoSubsidio', e.target.value ? Number(e.target.value) : null)}
            inputProps={{ min: 0, step: 0.01 }} />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Observaciones */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Observaciones
        </Typography>
        <TextField label="Observaciones" size="small" fullWidth multiline rows={2} sx={{ mb: 1.5 }}
          value={form.observaciones ?? ''}
          onChange={(e) => handleChange('observaciones', e.target.value)} />
        <TextField label="Documento Sustento" size="small" fullWidth
          value={form.documentoSustento ?? ''}
          onChange={(e) => handleChange('documentoSustento', e.target.value)} />

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
